import { Component, ElementRef, OnInit, AfterViewInit, AfterContentChecked, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationStart } from '@angular/router';

import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';

import { Subject, takeUntil } from 'rxjs';
import { NgxsModule, Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';

// Actions
import { ClearHTTPErrors } from '@app/store/app/states/ui/ui.actions';

// Blocks
import { HeaderBlock } from '@app/blocks/header/header.block';

// Utils
import { getNavItems, groupByCategory, NavItem } from '@app/utils/router/router.utils';

// Store
import { UIState } from '@app/store/app/states/ui/ui.state';

@Component({
  standalone: true,
  selector: 'app-layout',
  imports: [
    CommonModule,
    RouterModule,
    NgxsModule,
    MatSidenavModule,
    MatIcon,
    MatListModule,
    MatProgressSpinnerModule,
    HeaderBlock,
    MatExpansionModule,
  ],
  templateUrl: './app.layout.component.html',
  styleUrl: './app.layout.component.scss',
})
export class AppLayoutComponent implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {
  private destroy$ = new Subject<void>();

  protected categorizedItems: Record<string, NavItem[]> = {};
  protected isLoading = false;

  constructor(
    private readonly router: Router,
    private readonly appLayoutElementReference: ElementRef,
    private readonly toastr: ToastrService,
    private readonly store: Store,
  ) {
    // Router events subscription
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((ev) => {
      if (!(ev instanceof NavigationStart)) {
        return;
      }

      // this.httpAbortService.cancelRequests();
    });

    // UI Store subscription
    this.store
      .select(UIState.getLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading: boolean) => (this.isLoading = loading));
  }

  ngOnInit(): void {
    const routes = this.router.config;
    const navItems = getNavItems(routes);
    this.categorizedItems = groupByCategory(navItems);
  }

  ngAfterViewInit(): void {
    this.store
      .select(UIState.getHTTPErrorMessages)
      .pipe(takeUntil(this.destroy$))
      .subscribe((messages) => {
        if (!messages.length) {
          return void 0;
        }

        messages.forEach((message) =>
          this.toastr.error(message, `Request error!`, {
            positionClass: 'toast-bottom-right',
          }),
        );
        this.store.dispatch(new ClearHTTPErrors());
      });
  }

  ngAfterContentChecked(): void {
    const banner: HTMLDivElement | null =
      this.appLayoutElementReference.nativeElement.querySelector('app-header .banner');

    if (!banner) {
      return;
    }

    const { height = 0 } = banner.getBoundingClientRect();
    this.appLayoutElementReference.nativeElement.style.setProperty('--app-content-offset', `${64 + ~~height}px`);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
